/**
 * @author Andrés Camilo Ferreira Rodríguez
 * @Copyright (c) Infotrack. All rights reserved.
 */
import { useProveedor } from '@infotrack/presentacion-componentes/proveedorEstado';
import Texto from '@infotrack/presentacion-componentes/texto';
import { TipoFormulario } from '@infotrack/presentacion-transversales/interfacesComunes';
import { useInputState } from '@infotrack/presentacion-utilitarios/hooks';
import {
    manejadorDialogoGlobal,
    manejadorIndicadorCargaGlobal,
} from '@infotrack/presentacion-utilitarios/manejadoresComponentes';
import IBodega from 'Infotrack@Modelos/smartStock/bodegas/entidades/bodega';
import IResolucionesFacturaciones from 'Infotrack@Modelos/smartStock/resolucionesFacturaciones/entidades/resolucionesFacturaciones';
import { IEstadoGlobal } from 'Infotrack@Reductores/interfacesReductores';
import React from 'react';
import { connect } from 'react-redux';
import EntidadesResolucionesFacturacionesCM from '../controladorModelo/entidadesResolucionesFacturacionesCM';
import ResolucionesFacturacionesCM from '../controladorModelo/resolucionesFacturacionesCM';
import { IFormularioResolucionesFacturacionesCVProps, IInputsResolucionesFacturaciones } from '../interfaces';
import { acciones, IEstadoResolucionesFacturaciones } from '../reductorResolucionesFacturaciones';
import FormularioResolucionesFacturacionesVista from '../vista/formularioResolucionesFacturacionesVista';

const estadoInicial: IInputsResolucionesFacturaciones = {
    EmpresaId: '',
    AgenciaId: '',
    BodegaId: '',
    DescripcionResolucionFacturacion: '',
    FacturaInicio: '',
    FacturaFin: '',
    FacturaActual: '',
    FechaInicio: null,
    FechaFin: null,
    Estado: '',
};

const resolucionesFacturacionesCM = new ResolucionesFacturacionesCM();
const entidadesResolucionesFacturacionesCM = new EntidadesResolucionesFacturacionesCM();

const FormularioResolucionesFacturacionesCV: React.FunctionComponent<IFormularioResolucionesFacturacionesCVProps> = ({
    IdEmpresa,
    IdAgencia,
    IdUsuario,
}) => {
    const [{ estadoFormulario, tipoFormulario, entidadResolucionesFacturaciones }, dispatch] = useProveedor<
        IEstadoResolucionesFacturaciones
    >();
    const { alCambiarValor, setValor, reiniciar, valor } = useInputState<IInputsResolucionesFacturaciones>(
        estadoInicial
    );
    const [bodegas, setBodegas] = React.useState<IBodega[]>([]);

    React.useEffect(() => {
        if (entidadResolucionesFacturaciones && tipoFormulario !== TipoFormulario.Creacion) {
            setValor(entidadResolucionesFacturaciones);
        } else {
            reiniciar();
        }
    }, [entidadResolucionesFacturaciones]);

    React.useEffect(() => {
        cargarEntidadesResolciones();
    }, []);

    const cargarEntidadesResolciones = () => {
        entidadesResolucionesFacturacionesCM
            .consultarEntidadesFiltro({ AgenciaId: IdAgencia, UsuarioId: IdUsuario, EmpresaId: IdEmpresa })
            .then(({ bodegas }) => setBodegas(bodegas));
    };

    const cerrarFormulario = () => {
        dispatch({ type: acciones.CAMBIAR_ESTADO_FORMULARIO, payload: null });
    };
    const confirmarCreacionEdicionResolucionesFacturaciones = () => {
        if (resolucionesFacturacionesCM.validarEntidad(mapearDatos())) {
            manejadorDialogoGlobal({
                accionCancelar: () => manejadorDialogoGlobal({ estado: false }),
                accionConfirmar: crearEditarResolucionesFacturaciones,
                estado: true,
                mensaje: (
                    <Texto
                        id={
                            tipoFormulario === TipoFormulario.Creacion
                                ? 'alerta.confirmacionCreacion'
                                : 'alerta.confirmacionEdicion'
                        }
                    />
                ),
                mostrarCancelar: true,
                tipoDialogo: 'Advertencia',
                titulo: <Texto id="titulo.advertencia" />,
            });
        }
    };

    const crearEditarResolucionesFacturaciones = () => {
        manejadorDialogoGlobal({ estado: false });
        manejadorIndicadorCargaGlobal(true);
        if (tipoFormulario === TipoFormulario.Creacion) {
            resolucionesFacturacionesCM.crearResolucionesFacturaciones(mapearDatos()).then((respuesta) => {
                if (respuesta.data.Resultado) {
                    cerrarFormulario();
                    dispatch({ type: acciones.RECARGAR_TABLA });
                }
                manejadorIndicadorCargaGlobal(false);
            });
        } else {
            resolucionesFacturacionesCM.editarResolucionesFacturaciones(mapearDatos()).then((respuesta) => {
                if (respuesta.data.Resultado) {
                    cerrarFormulario();
                    dispatch({ type: acciones.RECARGAR_TABLA });
                }
                manejadorIndicadorCargaGlobal(false);
            });
        }
    };

    const alCambiarValores = ({
        target,
    }: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { checked, name, type, value } = target as EventTarget & HTMLInputElement;
        setValor({ ...valor, [name]: type === 'checkbox' ? checked : value });
    };

    const mapearDatos = (): IResolucionesFacturaciones => ({
        ...valor,
        EmpresaId: IdEmpresa,
        AgenciaId: IdAgencia,
        Estado: valor.Estado ? '1' : '0',
        ResolucionFacturacionId: Number(valor.ResolucionFacturacionId),
        BodegaId: Number(valor.BodegaId),
        FacturaInicio: Number(valor.FacturaInicio),
        FacturaFin: Number(valor.FacturaFin),
        FacturaActual: Number(valor.FacturaActual),
        FechaFin: valor.FechaFin ? valor.FechaFin : null,
        FechaInicio: valor.FechaInicio ? valor.FechaInicio : null,
    });

    return (
        <FormularioResolucionesFacturacionesVista
            alCambiarValor={alCambiarValores}
            accionCrearEditar={confirmarCreacionEdicionResolucionesFacturaciones}
            cambiarInfoFomulario={alCambiarValor}
            resolucionesFacturaciones={valor}
            cerrarFormulario={cerrarFormulario}
            estadoFormulario={estadoFormulario}
            tipoFormulario={tipoFormulario}
            entidadesResoluciones={{ bodegas }}
        />
    );
};

const estadoAPropiedades = ({
    estadoAutenticacion: { agenciaActual, usuarioInformacion },
}: IEstadoGlobal): IFormularioResolucionesFacturacionesCVProps => ({
    IdEmpresa: usuarioInformacion!.IdEmpresa,
    IdAgencia: agenciaActual!.IdAgencia,
    IdUsuario: usuarioInformacion!.IdUsuario,
});

export default connect<IFormularioResolucionesFacturacionesCVProps, null, any, IEstadoGlobal>(
    estadoAPropiedades,
    null
)(FormularioResolucionesFacturacionesCV);
